/* eslint-disable jsx-a11y/anchor-is-valid */

import LogicContext from '../../Context/LogicContext';
import { useEffect, useRef } from 'react';
import { useState } from 'react';
import { useContext } from 'react';
import { SetRatingListAtom, GetRatingListAtom } from '../../store/ratingAtom';
import { IoMdRefresh } from 'react-icons/io';
import { IoCloudDownloadOutline, IoSearch, IoClose } from 'react-icons/io5';
import Tooltip from '@mui/material/Tooltip';
import { useLocation, useNavigate } from 'react-router-dom';
import { customerService } from '../../services/customer.service';
import RatingTypeSelect from './RatingTypeSelect';
import GeneralCustomTable from '../../helpers/GeneralCustomTable';
import { formatCustomNumber } from '../../helpers/formatCustomNumber';
import moment from 'moment';
import Papa from 'papaparse'; // ← Added for reliable CSV export

const columns = [
  {
    title: 'Name',
    key: 'Transaction_Type_Name',
    dataIndex: 'Transaction_Type_Name',
    className: 'text-primary',
    sorter: (a, b) => (a?.Transaction_Type_Name ?? '').localeCompare(b?.Transaction_Type_Name ?? ''),
  },
  {
    title: 'Rating Value',
    key: 'Rating_Value',
    dataIndex: 'Rating_Value',
    sorter: (a, b) => (a?.Rating_Value ?? '') - (b?.Rating_Value ?? ''),
    render: record => <span>{formatCustomNumber(record)}</span>,
  },
  {
    title: 'Total Rating',
    key: 'Total_Rating',
    dataIndex: 'Total_Rating',
    sorter: (a, b) => (a?.Total_Rating ?? '') - (b?.Total_Rating ?? ''),
    render: record => <span>{formatCustomNumber(record)}</span>,
  },
];

function RatingMainPage() {
  const logicContext = useContext(LogicContext);
  const getRatingListAtom = GetRatingListAtom();
  const setRatingListAtom = SetRatingListAtom();

  const [tableList, setTableList] = useState([]);
  const [searchText, setSearchText] = useState('');

  const navigate = useNavigate();
  const isMounted = useRef(false);

  const { pathname } = useLocation();
  const url = pathname.split('/');
  const currModuleId = url[1];
  const currModuleName = url[2];
  const currUserMenu = url[3];

  useEffect(() => {
    document.title = 'Rating page';

    if (getRatingListAtom.rating_info?.length) {
      setTableList(getRatingListAtom.rating_info);
    }

    return () => {
      isMounted.current = false;
    };
  }, [getRatingListAtom.rating_info]);

  function refreshList() {
    customerService.getRatingList().then(response => {
      if (response?.data?.rating_info) {
        setRatingListAtom(response.data);
        setTableList(response?.data?.rating_info);
      }
    });
  }

  const onClearInput = () => {
    setSearchText('');
    setTableList(getRatingListAtom.rating_info || []);
  };

  const handleSearch = text => {
    setSearchText(text);
    if (text.trim() === '') {
      setTableList(getRatingListAtom.rating_info || []);
      return;
    }

    const searchTerm = text.toLowerCase();
    const filtered = (getRatingListAtom.rating_info || []).filter(item =>
      Object.values(item).some(value => value?.toString().toLowerCase().includes(searchTerm))
    );
    setTableList(filtered);
  };

  function handleRowClick(event) {
    const id = event['Transaction_type_Id'];
    navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${id}`);
  }

  const paginationOptions = {
    pageSize: 10,
    showSizeChanger: true,
    total: tableList.length,
  };

  // ====================== EXPORT CSV ======================
  const exportToCSV = () => {
    if (!tableList?.length) {
      logicContext.showToast('No data to export', 'warning');
      return;
    }

    const headers = columns.map(col => col.title);
    const keys = columns.map(col => col.dataIndex);

    const dataForExport = tableList.map(row => {
      const obj = {};
      keys.forEach((key, i) => {
        let value = row[key] ?? '';

        // Match table formatting
        if (key === 'Rating_Value' || key === 'Total_Rating') {
          value = formatCustomNumber(value);
        }

        obj[headers[i]] = value;
      });
      return obj;
    });

    const csv = Papa.unparse(dataForExport, { header: true });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `ratings_${moment().format('YYYY-MM-DD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    logicContext.showToast('✅ CSV exported successfully!', 'success');
  };
  // =======================================================

  return (
    <>
      <section className="@container">
        <section className="mt-2 w-full bg-white">
          {/* Top Actions: Toolbar */}
          <div className="flex flex-col gap-4 @xl:flex-row @xl:items-end @xl:justify-between">
            {/* Left side: Refresh & Filters */}
            <div className="flex w-full flex-col gap-3 @2xl:w-auto lg:flex-row lg:items-center">
              <div className="flex items-center gap-3">
                <Tooltip title="Reload">
                  <button
                    type="button"
                    onClick={refreshList}
                    className="shrink-0 rounded-xl border border-gray-200 p-2.5 text-gray-500 transition-all hover:border-blue-200 hover:bg-gray-50 hover:text-blue-600 active:scale-95"
                  >
                    <IoMdRefresh className="h-5 w-5" />
                  </button>
                </Tooltip>

                <Tooltip title="Total">
                  <div className="flex items-center whitespace-nowrap rounded-lg border border-blue-100 bg-blue-50/50 px-4 py-2.5 text-sm font-semibold text-blue-700 shadow-sm transition-colors hover:bg-blue-50">
                    <span className="mr-1">Total:</span>
                    <span>{tableList?.length}</span>
                  </div>
                </Tooltip>
              </div>

              {/* Uncomment if you want to add the RatingTypeSelect back */}
              {/* <div className="w-full flex-1 sm:w-auto sm:min-w-[180px]">
                    <RatingTypeSelect ... />
                  </div> */}
            </div>

            {/* Right side: Actions */}
            <div className="flex w-full items-center gap-3 @xl:w-auto">
              <button
                onClick={exportToCSV}
                className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-4 py-2.5 font-[Poppins] text-xs font-semibold text-gray-700 transition-all @xl:py-2.5 hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100 sm:flex-none"
              >
                <IoCloudDownloadOutline className="h-[18px] w-[18px] shrink-0 text-gray-500" />
                EXPORT CSV
              </button>
            </div>
          </div>

          <section className="mt-6">
            <div className="mb-4 flex flex-col justify-between gap-4 sm:items-center md:flex-row">
              {/* Table Search */}
              <div className="relative w-full sm:w-[350px]">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <IoSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search in table..."
                  value={searchText}
                  onChange={e => handleSearch(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Escape') {
                      onClearInput();
                    }
                  }}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-10 text-sm text-gray-700 placeholder-gray-400 outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                />
                {searchText && (
                  <button
                    onClick={onClearInput}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                  >
                    <IoClose className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>

            <GeneralCustomTable
              tableHeadList={columns}
              tableDataList={tableList}
              allowCheckBox={false}
              setSelectedRows={() => console.log('selection')}
              paginationOptions={paginationOptions}
              rowKey={record => record.Transaction_type_Id || record.Vendor_Id} // safer key
              handleRowClick={handleRowClick}
            />
          </section>
        </section>
      </section>
    </>
  );
}

export default RatingMainPage;
